/** Formatação e datas civis.
 *
 * Datas do domínio são strings `YYYY-MM-DD` e nunca viram `Date` local (isso desloca o dia
 * em fuso negativo). Onde precisa de aritmética, usa UTC. O timezone do usuário é problema
 * do servidor: ele decide qual dia é "hoje".
 */

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const MINUS = "−"; // menos tipográfico, alinha com os dígitos tabulares
const NBSP = " ";

/** R$ 1.234,56 — com menos de verdade e espaço que não quebra. */
export function money(v: number): string {
  return BRL.format(v).replace(/\s/g, NBSP).replace("-", MINUS);
}

/** Valor absoluto formatado, para quando o sinal já está na tela. */
export function moneyAbs(v: number): string {
  return money(Math.abs(v));
}

/** 1,2k · 340 · −336 — para a fita e os mini-indicadores, onde não cabe o valor inteiro. */
export function short(v: number): string {
  const a = Math.abs(v);
  const s = a >= 1000 ? (a / 1000).toFixed(a >= 10000 ? 0 : 1).replace(".", ",") + "k" : a.toFixed(0);
  return (v < 0 ? MINUS : "") + s;
}

export function signed(v: number, kind: "income" | "out"): string {
  return (kind === "income" ? "+" : MINUS) + money(Math.abs(v));
}

const WD = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

/** O fuso do app. O motor projeta em America/Cuiaba (`today_local()` na API), então o
 * front não pode decidir o dia pelo UTC do navegador: entre 20h e meia-noite em Cuiabá o
 * UTC já virou, e o app pedia a projeção a partir de amanhã — os itens atrasados, que o
 * motor põe em "hoje", caíam fora da janela e desapareciam da lista "A pagar". */
export const TZ = "America/Cuiaba";

/** Hoje no fuso do app, em YYYY-MM-DD (en-CA formata exatamente assim). */
export const hojeLocal = (): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

/** Lê dinheiro digitado em pt-BR E no jeito que o teclado do celular escreve.
 *
 * O parser anterior era `replace(/\./g,"").replace(",",".")`: todo ponto era separador de
 * milhar. Quem digitasse `1234.56` lançava **R$ 123.456,00**, e `50.5` virava R$ 505,00 —
 * calado, com o botão habilitado. Num app de dinheiro isso é o defeito mais caro possível,
 * e é o erro mais provável de todos, porque metade dos teclados oferece ponto.
 *
 * A regra desfaz a ambiguidade do jeito que uma pessoa faria:
 * - tem vírgula → a vírgula é o decimal e os pontos são milhar (pt-BR puro);
 * - só pontos, e o último grupo tem exatamente 3 dígitos → milhar ("1.234" = mil e tanto);
 * - só pontos, e o último grupo tem 1 ou 2 dígitos → decimal ("50.5" = cinquenta e meio).
 *
 * `1.234` continua sendo mil duzentos e trinta e quatro, como se escreve aqui. Para não
 * restar dúvida, quem usa este parser mostra ao lado o valor que entendeu.
 */
export function parseMoney(entrada: string): number {
  const limpo = (entrada ?? "").trim().replace(/[^\d.,-]/g, "");
  if (!limpo) return 0;
  if (limpo.includes(",")) {
    return Number(limpo.replace(/\./g, "").replace(",", ".")) || 0;
  }
  const partes = limpo.split(".");
  if (partes.length > 1) {
    const ultimo = partes[partes.length - 1];
    if (ultimo.length !== 3) return Number(partes.join("").slice(0, -ultimo.length) + "." + ultimo) || 0;
  }
  return Number(limpo.replace(/\./g, "")) || 0;
}

export const parseDay = (s: string) => new Date(s + "T00:00:00Z");
export const toISO = (d: Date) => d.toISOString().slice(0, 10);

/** 'seg' — dia da semana de uma data civil. */
export const weekday = (s: string) => WD[parseDay(s).getUTCDay()];
export const isWeekend = (s: string) => [0, 6].includes(parseDay(s).getUTCDay());

/** '07/09' */
export const dayMonth = (s: string) => s.slice(8, 10) + "/" + s.slice(5, 7);
/** '07/09/2027' — para datas longe, onde omitir o ano engana (o horizonte vai a 365 dias). */
export const dayMonthYear = (s: string) => dayMonth(s) + "/" + s.slice(0, 4);
/** '07' */
export const dayOnly = (s: string) => s.slice(8, 10);
/** 'Setembro 2026' */
export const monthLabel = (s: string) => `${MONTHS[Number(s.slice(5, 7)) - 1]} ${s.slice(0, 4)}`;

export function addDays(s: string, n: number): string {
  const d = parseDay(s);
  d.setUTCDate(d.getUTCDate() + n);
  return toISO(d);
}

export function addMonths(s: string, n: number): string {
  const d = parseDay(s);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + n);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return toISO(d);
}

/** Primeiro e último dia do mês de uma data civil. */
export function monthRange(s: string): { first: string; last: string } {
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7));
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, "0");
  return { first: `${s.slice(0, 4)}-${mm}-01`, last: `${s.slice(0, 4)}-${mm}-${last}` };
}

export const daysInMonth = (s: string) =>
  new Date(Date.UTC(Number(s.slice(0, 4)), Number(s.slice(5, 7)), 0)).getUTCDate();

export function daysBetween(from: string, to: string): number {
  return Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / 86400000);
}

/** Soma em centavos e arredonda uma vez, para não acumular erro de float na tela. */
export function sumMoney(values: number[]): number {
  return values.reduce((acc, v) => acc + Math.round(v * 100), 0) / 100;
}

/** Rótulos de status por tipo de lançamento, como na v2. */
export function statusLabels(kind: "income" | "expense" | "transfer", bucket?: string | null) {
  if (kind === "income") return { pending: "Pendente", cleared: "Recebido" };
  if (bucket === "savings" || kind === "transfer") return { pending: "Planejado", cleared: "Investido" };
  if (bucket === "daily") return { pending: "Previsto", cleared: "Realizado" };
  return { pending: "Pendente", cleared: "Pago" };
}

export function stampLabel(tx: {
  status: string;
  estimated?: boolean;
  kind?: string;
  bucket?: string | null;
  overdue?: boolean;
  scenario_id?: string | null;
}): { text: string; cls: string } {
  if (tx.scenario_id) return { text: "Simulado", cls: "sim" };
  if (tx.overdue) return { text: "Atrasado", cls: "late" };
  if (tx.estimated) return { text: "Estimado", cls: "est" };
  const labels = statusLabels((tx.kind as "income") ?? "expense", tx.bucket);
  if (tx.status === "cleared") return { text: labels.cleared, cls: "paid" };
  if (tx.status === "pending") return { text: labels.pending, cls: "pend" };
  if (tx.status === "skipped") return { text: "Pulado", cls: "plan" };
  return { text: "Planejado", cls: "plan" };
}

export const KIND_LABEL: Record<string, string> = {
  income: "Entrada",
  expense: "Saída",
  transfer: "Economia",
};

/** A coluna em que o lançamento entra na tabela de dias. Diário é bucket, não tipo. */
/** Origens de transferência que são pagamento de dívida, não poupança. */
const PAGA_DIVIDA = ["statement_payment", "loan_payment"];

export function column(tx: {
  kind: string;
  bucket?: string | null;
  source?: string | null;
}): "in" | "out" | "dy" | "sv" {
  if (tx.kind === "income") return "in";
  if (tx.kind === "transfer") {
    // Pagar a fatura do cartão ou a parcela do crediário é transferência no banco de dados
    // (sai da conta, entra no passivo), mas para quem lê é SAÍDA. Sem esta distinção, uma
    // fatura de R$ 3.000 aparecia como R$ 3.000 de "Economia" no mês.
    return PAGA_DIVIDA.includes(tx.source ?? "") ? "out" : "sv";
  }
  return tx.bucket === "daily" ? "dy" : "out";
}
